import { z } from 'zod'
import { YearMonth } from './YearMonth'
import { ContractEmployee, ContractRemuneration } from './Contract'
import { EditorRoleEnum } from './EditorRole'
import { DIFF_AS_ONE } from '../diffConstants'

export const oneTimePaymentType = [
  'HourlyWage',
  'OverTimePayment',
  'Sunday',
  'Holiday',
  'Night',
  'Prepayment',
  'BonusPayment',
  'Commission',
  'OvernightStayExpenses',
  'AdditionalMealExpenses',
  'VacationPayment',
  'Misc',
] as const

export const OneTimePaymentTypeEnum = z.enum(oneTimePaymentType)

export type OneTimePaymentType = (typeof oneTimePaymentType)[number]

export const OvernightStayExpenses = z
  .object({
    country: z.string().nullable(),
    city: z.string().nullable(),
    nights: z.number().nullable(),
    ownVehicle: z.boolean().nullable(),
  })
  .strict()
  .describe(DIFF_AS_ONE)

export type OvernightStayExpenses = z.infer<typeof OvernightStayExpenses>

export const AdditionalMealExpenses = z
  .object({
    country: z.string().nullable(),
    city: z.string().nullable(),
    halfDays: z.number().nullable(),
    fullDays: z.number().nullable(),
    breakfasts: z.number().nullable(),
    dinners: z.number().nullable(),
    additionalPayment: z.number().nullable(),
  })
  .strict()
  .describe(DIFF_AS_ONE)

export type AdditionalMealExpenses = z.infer<typeof AdditionalMealExpenses>

export const differenceDecisionValues = ['carryOver', 'notPaid'] as const
export const DifferenceDecisionEnum = z.enum(differenceDecisionValues)
export type DifferenceDecision = (typeof differenceDecisionValues)[number]

/**
 * Hours of a time-tracking hourly wage that were moved to a later payroll.
 * `paymentUid` and `yearMonth` name the payment and payroll month where the
 * hours were first carried over and stay the same when they are carried over
 * again. `reason` is the reason given for the latest carry-over.
 */
export const CarryOverLot = z
  .object({
    paymentUid: z.string(),
    yearMonth: YearMonth,
    hours: z.number(),
    reason: z.string().nullable(),
  })
  .strict()

export type CarryOverLot = z.infer<typeof CarryOverLot>

export const OneTimePayment = z
  .object({
    deleted: z.boolean().default(false),

    companyUid: z.string(),
    contractUid: z.string(),
    employee: ContractEmployee.describe(DIFF_AS_ONE),
    remuneration: ContractRemuneration.describe(DIFF_AS_ONE),

    type: OneTimePaymentTypeEnum,
    customTypeId: z.preprocess(
      (value) => (value === '' ? null : value),
      z
        .string()
        .regex(/^\d{1,5}$/)
        .nullable()
        .catch(null)
    ),
    yearMonth: YearMonth,
    description: z.string().nullable(),

    hourlyWage: z.number().nullable(),
    hours: z.number().nullable(),
    factor: z.number().nullable(),
    payment: z.number().nullable(),

    overnightStayExpenses: OvernightStayExpenses.nullable(),
    additionalMealExpenses: AdditionalMealExpenses.nullable(),

    // Source tracking for idempotency
    sourceSystem: z.string().nullable(),
    sourceId: z.string().nullable(),

    /**
     * Hourly wage from time tracking: sourceSystem 'timeTracking', one payment
     * per contract and payroll month (id from `hourlyWagePaymentUid`). Every
     * other payment keeps the defaults.
     *
     * - recordedHours: hours logged in the time entries the payment covers.
     * - carryOverFrom: hours carried over from earlier payroll months that this
     *   payment includes. `hours` is what is paid, at `hourlyWage`, so
     *   recordedHours + carryOverFrom = hours + carriedOver + hours not paid.
     * - differenceDecision: what the employer chose for the hours not paid,
     *   with the required `adjustmentReason`.
     * - carriedOver: the hours moved to the next payroll, by the month they
     *   were first carried over in. Hours from this payment's own entries
     *   reference this payment.
     */
    recordedHours: z.number().nullable().default(null),
    carryOverFrom: z.array(CarryOverLot).default([]),
    differenceDecision: DifferenceDecisionEnum.nullable().default(null),
    adjustmentReason: z.string().nullable().default(null),
    carriedOver: z.array(CarryOverLot).default([]),
    /**
     * Holiday pay (sourceSystem 'holidayPay'): the public holidays (YYYY-MM-DD)
     * the payment pays. A holiday listed on any holiday pay, deleted ones too,
     * is never paid again. Empty on every other payment.
     */
    holidayDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).default([]),

    createdAt: z.number(),
    createdBy: z.string(),
    createdByName: z.string(),
    createdByRole: EditorRoleEnum,

    updatedAt: z.number(),
    updatedBy: z.string(),
    updatedByName: z.string(),
    updatedByRole: EditorRoleEnum,

    confirmed: z.boolean(),
    confirmedAt: z.number().nullable(),
    confirmedBy: z.string().nullable(),
    confirmedByName: z.string().nullable(),
    confirmedByRole: EditorRoleEnum.nullable(),

    settled: z.boolean(),
    settledYearMonth: z.string().nullable(),
    settledAt: z.number().nullable(),
    settledBy: z.string().nullable(),
    settledByName: z.string().nullable(),
    settledByRole: EditorRoleEnum.nullable(),
  })
  .strict()
  .describe(DIFF_AS_ONE)

export type OneTimePayment = z.infer<typeof OneTimePayment>
